import { QueryClient } from '@tanstack/react-query';
import {
  GlobalPermission,
  SystemPermission_Permission,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/user_pb';
import { redirect } from 'react-router';
import { authService } from '~/services/auth/auth-service';
import { SKIP_SIGNIN } from '~/services/environment/environment';
import { authUserQuery } from '~/shared/api/auth-user.queries';
import { TERMS_AND_CONDITIONS_VERSION } from '~/shared/auth/internal/user-constants';
import { createRoutes, RouteValues } from '~/shared/models/create-routes';
import { UserRole } from '~/shared/models/system/RolesModel';
import { AuthUserModel } from '~/shared/models/auth-user/AuthUserModel';

export { TERMS_AND_CONDITIONS_VERSION };

function urlStringNoOrigin(url: URL) {
  return url.toString().replace(url.origin, '');
}

function createRedirectUrl(requestUrlString: string) {
  const requestUrl = new URL(requestUrlString);
  requestUrl.hash = window.location.hash;
  return urlStringNoOrigin(requestUrl);
}

function createUrl(redirectUrl: string, route: RouteValues) {
  const loginUrl = new URL(route, 'http://localhost');
  loginUrl.searchParams.set('redirect_url', redirectUrl);
  return urlStringNoOrigin(loginUrl);
}

export function hasAcceptedTerms(userTermsVersion: number | undefined) {
  return userTermsVersion === TERMS_AND_CONDITIONS_VERSION;
}

async function redirectToLogin(
  queryClient: QueryClient,
  loginUrl: string,
): Promise<never> {
  queryClient.clear();
  await authService.signOut();
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect(loginUrl);
}

async function requireValidIdentityToken(
  queryClient: QueryClient,
  loginUrl: string,
) {
  let token: string | null;
  try {
    token = await authService.getIdentityToken();
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes('auth/user-token-expired')
    ) {
      await redirectToLogin(queryClient, loginUrl);
    }
    throw error;
  }

  if (!token) {
    await redirectToLogin(queryClient, loginUrl);
  }
}

export async function getAuthUser(queryClient: QueryClient, request: Request) {
  await authService.initialized;

  const { isSignedIn } = authService;
  const loginUrl = createUrl(
    createRedirectUrl(request.url),
    createRoutes.SignIn,
  );

  if (!isSignedIn && !SKIP_SIGNIN) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect(loginUrl);
  }

  if (isSignedIn) {
    await requireValidIdentityToken(queryClient, loginUrl);
  }

  return await queryClient.ensureQueryData(authUserQuery);
}

export async function checkAuthorizationWithPredicate(
  queryClient: QueryClient,
  request: Request,
  authPredicate: (authUser: AuthUserModel) => boolean,
) {
  const authUser = await getAuthUser(queryClient, request);

  if (!authUser || !authPredicate(authUser)) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect(createRoutes.Unauthorized);
  }

  if (!hasAcceptedTerms(authUser.profile.lastAcceptedTermsAndConditions)) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect(createRoutes.TermsConditions);
  }

  return null;
}

export function checkAuthorization(queryClient: QueryClient, request: Request) {
  return checkAuthorizationWithPredicate(queryClient, request, () => true);
}

export async function checkIfUserHasPermissionForSystem(
  queryClient: QueryClient,
  request: Request,
  systemId: string,
  requestedUserPermissions:
    | SystemPermission_Permission
    | SystemPermission_Permission[],
) {
  const authUser = await getAuthUser(queryClient, request);

  const permissions = Array.isArray(requestedUserPermissions)
    ? requestedUserPermissions
    : [requestedUserPermissions];

  return permissions.some((permission) =>
    authUser?.hasPermissionForSystemId(permission, systemId),
  );
}

export function requireUserPermissionForSystemId(
  queryClient: QueryClient,
  request: Request,
  requestedUserPermissions:
    | SystemPermission_Permission
    | SystemPermission_Permission[],
  systemId: string,
) {
  return checkAuthorizationWithPredicate(queryClient, request, (authUser) => {
    const permissions = Array.isArray(requestedUserPermissions)
      ? requestedUserPermissions
      : [requestedUserPermissions];

    return permissions.some((permission) =>
      authUser.hasPermissionForSystemId(permission, systemId),
    );
  });
}

export async function checkIfUserHasGlobalPermission(
  queryClient: QueryClient,
  request: Request,
  requestedGlobalPermission: GlobalPermission,
) {
  const authUser = await getAuthUser(queryClient, request);

  return !!authUser?.hasGlobalPermission(requestedGlobalPermission);
}

export function requireUserGlobalPermission(
  queryClient: QueryClient,
  request: Request,
  requestedGlobalPermission: GlobalPermission,
) {
  return checkAuthorizationWithPredicate(queryClient, request, (authUser) =>
    authUser.hasGlobalPermission(requestedGlobalPermission),
  );
}

export function ensureUserCanSeeIssuesList(
  queryClient: QueryClient,
  request: Request,
) {
  return checkAuthorizationWithPredicate(
    queryClient,
    request,
    (authUser) => authUser.canSeeIssuesList,
  );
}

export function requireUserRoleForSystemId(
  queryClient: QueryClient,
  request: Request,
  requestedUserRoles: UserRole | UserRole[],
  systemId: string,
) {
  return checkAuthorizationWithPredicate(queryClient, request, (authUser) => {
    const roles = Array.isArray(requestedUserRoles)
      ? requestedUserRoles
      : [requestedUserRoles];

    return roles.some((role) => authUser.hasRoleForSystemId(role, systemId));
  });
}

export async function loadAuthUser(queryClient: QueryClient) {
  await authService.initialized;
  if (authService.isSignedIn) {
    await requireValidIdentityToken(queryClient, createRoutes.SignIn);
    await queryClient.fetchQuery(authUserQuery);
  }
}
